/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/ExpertDashboard.tsx.
 */

import React, { useEffect, useState } from "react";
import SidebarExpert from "../../../components/Layout/SidebarExpert";
import DashboardHeader from "../../../components/Layout/DashboardHeader";
import DashboardFooter from "../../../components/Layout/DashboardFooter";
import "./Expert.css";

import ExpertAssignedMaterials from "./AssignedMaterials/ExpertAssignedMaterials";
import ExpertReviewSubmissions from "./Submissions/ExpertReviewSubmissions";
import ExpertReviewPage from "./Review/ExpertReviewPage";
import ExpertProfilePage from "./Profile/ExpertProfilePage";
import ExpertHome from "./Home/ExpertHome";
import ExpertAllDataset from "./AllDataset/ExpertAllDataset";

import axiosClient from "../../../api/axiosClient";

const ExpertDashboard: React.FC = () => {
  /** ACTIVE SECTION FROM SIDEBAR */
  const [activeSection, setActiveSection] = useState("Home");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  /** MATERIAL SELECTED FOR REVIEWING SUBMISSIONS */
  const [materialId, setMaterialId] = useState<number | null>(null);

  /** SUBMISSION SELECTED INSIDE REVIEW SUBMISSIONS */
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

  /** Load expert assigned material once */
  useEffect(() => {
    axiosClient.get("/expert/materials").then((res) => {
      if (res.data.success && res.data.data.length > 0) {
        setMaterialId(res.data.data[0].id);
      }
    });
  }, []);

  /** MAIN CONTENT RENDERING */
  const renderContent = () => {
    // If reviewing individual submission → show review page
    if (selectedSubmissionId) {
      return (
        <ExpertReviewPage
          submissionId={selectedSubmissionId}
          onBack={() => setSelectedSubmissionId(null)}
        />
      );
    }

    switch (activeSection) {
      case "Home":
        return (
          <ExpertHome
            onNavigateToMaterials={() => setActiveSection("My Materials")}
            onNavigateToReviews={() => {
              if (materialId) {
                setActiveSection("Review Submissions");
              } else {
                setActiveSection("My Materials");
              }
            }}
          />
        );

      case "My Materials":
        return (
          <ExpertAssignedMaterials
            onOpenSubmissions={(matId: number) => {
              setMaterialId(matId);
              setActiveSection("Review Submissions");
            }}
          />
        );

      case "Review Submissions":
        if (!materialId) {
          return <p style={{ padding: "1rem" }}>No material assigned yet.</p>;
        }

        return (
          <ExpertReviewSubmissions
            materialId={materialId}
            onBack={() => setActiveSection("My Materials")}
            onOpenReview={(submissionId: string) => {
              setSelectedSubmissionId(submissionId);
            }}
          />
        );

      case "All Datasets":
        return (
          <ExpertAllDataset
            materialId={materialId}
          />
        );

      case "Profile":
        return <ExpertProfilePage />;

      default:
        return <div>Section: {activeSection}</div>;
    }
  };

  return (
    <div className="dashboard-container">
      <DashboardHeader />

      <div className="dashboard-body">
        {/* Sidebar */}
        <div className={`sidebar-wrapper ${isSidebarOpen ? "open" : ""}`}>
          <SidebarExpert
            activeSection={activeSection}
            onSelect={(section) => {
              setActiveSection(section);
              setSelectedSubmissionId(null); // Reset review view when switching
              setIsSidebarOpen(false);
            }}
          />
        </div>

        {/* MOBILE TOGGLE */}
        <button className="menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
          ☰
        </button>

        {/* Main content area */}
        <main className="dashboard-content">
          <div className="content-wrapper">{renderContent()}</div>
        </main>
      </div>

      <DashboardFooter />
    </div>
  );
};

/** React component — Expert Dashboard. */
export default ExpertDashboard;